/**
 * Catalogue of the Chat.co Security Series webinars.
 *
 * Adding a new session is a data change, not a page change: append an entry
 * here, drop its recording under `public/videos/webinars/<slug>/`, and the
 * /webinars hub picks it up. Past sessions get a replay page at
 * /webinars/<slug>; upcoming ones link to their registration page.
 */

export type WebinarTakeaway = {
  label: string;
  title: string;
  body: string;
};

export type Webinar = {
  id: string;
  slug: string;
  /** Session number within the six-part series. */
  session: number;
  title: string;
  /** Rendered as the second, gold line of the hero heading. */
  titleAccent?: string;
  summary: string;
  /** ISO date — drives sorting and the <time> element. */
  isoDate: string;
  date: string;
  time: string;
  timeZone: string;
  duration: string;
  status: 'past' | 'upcoming';
  /** Full-length recording, relative to /public. Past sessions only. */
  replay?: string;
  /**
   * Seconds into `replay` where the talk actually starts, skipping the waiting
   * room. The player opens here; the viewer can still scrub back to 0:00.
   */
  replayStartsAt?: number;
  /** The rules the session walked through, for its "what we covered" grid. */
  takeaways?: WebinarTakeaway[];
  /** Registration page for sessions that have not happened yet. */
  registerPath?: string;
};

export const SERIES_LENGTH = 6;

export const webinars: Webinar[] = [
  {
    id: 'using-ai-safely-2026-09-18',
    slug: 'using-ai-safely',
    session: 1,
    title: 'Using AI Safely',
    titleAccent: 'in Your Business',
    summary:
      'A practical 30-minute workshop for small businesses: the traffic-light rule for what you share with AI, and four rules that keep client data out of the wrong tool.',
    isoDate: '2026-09-18',
    date: 'Friday, September 18, 2026',
    time: '12:00pm',
    timeZone: 'Eastern Time (ET)',
    duration: '30 minutes',
    status: 'past',
    replay: '/videos/webinars/using-ai-safely/replay.mp4',
    // The first three minutes are the waiting room before we start speaking.
    replayStartsAt: 180,
    takeaways: [
      {
        label: 'Rule 1 · Who can get in',
        title: 'A password on its own is not enough',
        body: 'Passwords get reused, one old breach exposes the workspace, and nobody notices until money moves. Look for one-time passcodes, single sign-on, and sessions you can revoke.',
      },
      {
        label: 'Rule 2 · Where your data goes',
        title: 'Know what happens to what you type',
        body: 'Consumer tools may keep what you paste, and a client contract can end up training a public model. Look for no training on your documents, zero data retention, and encryption in transit and at rest.',
      },
      {
        label: 'Rule 3 · Who sees what',
        title: 'Not everyone needs to see everything',
        body: 'One shared login means a new starter inherits the lot and a leaver keeps access nobody revokes. Look for per-person accounts and roles, separate workspaces, and removal that actually removes access.',
      },
      {
        label: 'Rule 4 · Trust but verify',
        title: 'Never act on an answer you cannot check',
        body: 'AI writes fluently even when it is wrong, and staff quote it to clients as fact. Look for answers drawn from your own documents, each reply citing its source, one click to the passage behind it.',
      },
    ],
  },
];

/** Sessions announced on stage but not yet scheduled — shown as "what's next". */
export const plannedSessions = [
  { session: 2, title: 'Voice cloning & payment fraud' },
  { session: 3, title: 'Choosing AI vendors — ten questions' },
  { session: 4, title: 'Your one-page AI policy' },
];

/** Where a session lives: its replay page once past, its sign-up page before. */
export const webinarPath = (event: Webinar) =>
  event.status === 'past' ? `/webinars/${event.slug}` : (event.registerPath ?? '/webinars');

const byDateDesc = (a: Webinar, b: Webinar) => b.isoDate.localeCompare(a.isoDate);

export const pastWebinars = webinars.filter((w) => w.status === 'past').sort(byDateDesc);

export const upcomingWebinars = webinars
  .filter((w) => w.status === 'upcoming')
  .sort((a, b) => a.isoDate.localeCompare(b.isoDate));

export const featuredWebinar = upcomingWebinars[0] ?? pastWebinars[0];
